/**
 * The `drawing` namespace, in English — the annotations on `components/AxonometryDrawing`.
 *
 * It is its own namespace rather than a corner of `landing` because the drawing has two
 * callers, the landing hero and the entrance's white half, and a label that lived in one
 * screen's catalog would be a label the other screen borrows.
 *
 * These are annotations on an architectural drawing, so they are set the way a drawing sets
 * them: uppercase, terse, no sentence. Storey labels name the storey first and its use second
 * — that is the order a section drawing reads in, and it survives being scanned rather than
 * read. Keep them short: they sit on a leader line inside a 400-unit sheet, and a label that
 * runs long walks left across the building it is pointing at.
 *
 * NUMBERS ARE NOT HERE, with one exception. `±0,000`, `20 m` and the rest are literals in the
 * component: `lib/i18n/formats.ts` has no number helper, so there is nothing to format them
 * with, and a drawing carries the continental spelling (comma decimal, thin-space thousands)
 * in every language it is printed in. `dimension.storey` is in the catalog anyway — it is the
 * one number a locale might genuinely want to respell, and putting it here costs nothing.
 */

export const drawingEn = {
  'level.roof': 'ROOF / TERRACE',
  'level.penthouse': '5TH FLOOR SET BACK',
  'level.flats': 'FLOORS 2–4 APARTMENTS',
  'level.ground': 'GROUND FLOOR SHOP + ENTRANCE',

  'courtyard': 'COURTYARD',
  'street': 'STREET',
  'rooflights': 'ROOFLIGHTS',

  /* The north point. One letter, and it is the initial of the word for north in the reader's
     own language — `S` for *sever* in Slovak, `N` in English and German. */
  'north': 'N',

  'scale': 'SCALE 1:200',

  /* The storey height in millimetres, the unit a building is dimensioned in. The separator is
     a thin space, which is what a drawing uses and what keeps `3 150` from reading as two
     numbers. */
  'dimension.storey': '3 150',

  /* ---------------------------------------------------------------- the two sheets
     `components/PlanSheetDrawing` and `components/SectionSheetDrawing`. Everything under
     `sheet.` is drawn by both of them — a title block is a title block — and everything under
     `plan.`, `section.` and `elevation.` belongs to one sheet.

     **The slot is the sheet's own geometry.** These labels sit in ruled cells and inside rooms
     that are 30 units across, so a word that overruns its cell is a bug rather than a wrap.
     German is the longest of the three and every cell below was budgeted for it: keep it
     compact, and check `node scripts/check-expansion.mjs` before adding a long one.

     No colons. A micro-label sits above or beside its value and the colon would have to be
     re-typeset in three languages for no gain. */

  'sheet.scale': 'SCALE 1:100',
  'sheet.project': 'REZIDENCIA B APARTMENTS',
  'sheet.rev.column.rev': 'REV',
  'sheet.rev.column.change': 'DESCRIPTION',
  'sheet.tb.project': 'PROJECT',
  'sheet.tb.object': 'BLOCK',
  'sheet.tb.stage': 'STAGE',
  'sheet.tb.sheet': 'DRAWING',
  'sheet.tb.part': 'PART',
  'sheet.tb.scale': 'SCALE',
  'sheet.tb.date': 'DATE',
  'sheet.tb.number': 'DRAWING NO.',
  'sheet.tb.version': 'VERSION',
  /* The one line on either sheet that is not drafting convention — it is the claim the product
     makes, set small in the corner of the title block where a CAD stamp normally goes. */
  'sheet.tb.managed': 'drawing controlled in Kalqa',

  /* --- The plan sheet --- */

  'plan.sheetName': 'GROUND FLOOR PLAN',
  'plan.part': 'ARCHITECTURE AND BUILDING',

  'plan.room.kitchen': 'KITCHEN',
  'plan.room.bedroom': 'BEDROOM',
  'plan.room.living': 'LIVING ROOM',
  'plan.room.bathroom': 'BATHROOM',
  'plan.room.wc': 'WC',
  'plan.room.loggia': 'LOGGIA',
  'plan.room.stair': 'STAIRS',
  'plan.room.lift': 'LIFT',
  'plan.room.hall': 'HALL',
  'plan.room.lobby': 'LOBBY',

  'plan.key.heading': 'KEY',
  'plan.key.masonry': 'MASONRY 300–600',
  'plan.key.partition': 'PARTITION 250 / GLAZING',
  'plan.key.version': 'CHANGE: VERSION r04',
  'plan.key.comment': 'COMMENT IN KALQA',

  /* The version history. `r01`–`r04` and the dates are identifiers and stay in the component;
     only what changed is a sentence. */
  'plan.rev.1': 'FIRST ISSUE',
  'plan.rev.2': 'CORE REVISED',
  'plan.rev.3': 'PARTITIONS MOVED B 1.3',
  'plan.rev.4': 'WINDOW 1800/1500 → 2200/1500',

  'plan.note.heading': 'NOTE',
  'plan.note.dimensions': 'DIMENSIONS IN mm. VERIFY ON SITE.',
  'plan.note.validity': 'VALID ONLY WITH REV. r04.',

  'plan.schedule.heading': 'FLAT AREAS: GROUND FLOOR',
  'plan.schedule.flat': 'FLAT',
  'plan.schedule.layout': 'LAYOUT',
  'plan.schedule.area': 'FLOOR AREA',
  'plan.schedule.aspect': 'ASPECT',
  'plan.schedule.type3': '3-ROOM',
  'plan.schedule.type2': '2-ROOM',
  /* Compass points, and they are initials of the reader's own words for them — `W / S` in
     English and German, `Z / J` in Slovak. */
  'plan.schedule.aspect.ws': 'W / S',
  'plan.schedule.aspect.ne': 'N / E',
  'plan.schedule.aspect.es': 'E / S',

  /* --- The section sheet --- */

  'section.name': 'SECTION A–A',
  'section.sheetName': 'SECTION A–A, STREET ELEVATION',
  'elevation.name': 'STREET ELEVATION',

  'section.label.lift': 'LIFT',
  /* `TERRAIN` rather than `GROUND`: the storey below is already called the ground floor, and
     two things on one drawing called the same word is how a drawing gets misread. */
  'section.label.terrain': 'TERRAIN',
  'section.label.footing': 'STRIP FOOTING',

  /* Storey names, in the shortest form each language actually writes on a section: Slovak
     numbers them `1.PP … 4.NP`, German `UG / EG / 1. OG`, English by name. They sit in a slot
     about twenty units wide between the west wall and the spine wall, so none of them may run
     to a phrase. */
  'section.storey.basement': 'BASEMENT',
  'section.storey.ground': 'GROUND',
  'section.storey.first': '1ST',
  'section.storey.second': '2ND',
  'section.storey.third': '3RD',
  'section.storey.attic': 'ATTIC',
} as const;
